import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Save, Trash2, Loader2, ChevronUp, ChevronDown, Download } from 'lucide-react';
import { api } from '@/api/client';

/**
 * The fixed list of outcomes an agent may record against a call.
 *
 * A dropdown rather than free text because the point of this list is to be COUNTED — "how many
 * customers refused delivery this week" is unanswerable when forty agents each phrase it their own
 * way. The free-text remark on the call is untouched and still carries the detail; this is the
 * category beside it.
 *
 * Order matters: it is the order of the dropdown, so the outcomes used fifty times a day belong at
 * the top. Renaming an entry does NOT rewrite the calls already saved under the old wording — the
 * call stores the TEXT, which is what keeps a historic report honest about what was actually chosen.
 */
interface IvrRemark { id: number; name: string }

const DEFAULTS: IvrRemark[] = [
  { id: 1, name: 'Customer will accept delivery' },
  { id: 2, name: 'Customer asked to re-attempt' },
  { id: 3, name: 'Customer asked for a specific date' },
  { id: 4, name: 'Customer not reachable' },
  { id: 5, name: 'Number switched off' },
  { id: 6, name: 'Wrong / invalid number' },
  { id: 7, name: 'Customer refused the order' },
  { id: 8, name: 'Customer wants to cancel' },
  { id: 9, name: 'Address incorrect / incomplete' },
  { id: 10, name: 'Customer out of station' },
  { id: 11, name: 'COD amount not ready' },
  { id: 12, name: 'Customer will self-collect' },
  { id: 13, name: 'Customer wants open delivery' },
  { id: 14, name: 'Already received the order' },
  { id: 15, name: 'Call disconnected / no response' },
];

export function IvrRemarksPanel() {
  const qc = useQueryClient();
  const [rows, setRows] = useState<IvrRemark[]>([]);
  const [dirty, setDirty] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'ivr_remarks'],
    queryFn: async () => {
      try { return (await api.get<{ items?: IvrRemark[] }>('/settings/ivr_remarks')).data ?? null; }
      catch { return null; }
    },
  });

  useEffect(() => {
    if (data && Array.isArray((data as any).items)) setRows((data as any).items);
    else if (data === null) setRows([]);
  }, [data]);

  const save = useMutation({
    mutationFn: async () => {
      const clean = rows.map((r) => ({ id: Number(r.id), name: String(r.name ?? '').trim() })).filter((r) => r.name);
      if (!clean.length) throw new Error('Add at least one remark before saving.');
      const names = new Set<string>();
      for (const r of clean) {
        const key = r.name.toLowerCase();
        // Two identical entries make the dropdown unusable and the report meaningless.
        if (names.has(key)) throw new Error(`"${r.name}" is in the list twice — remove one.`);
        names.add(key);
      }
      return (await api.put('/settings/ivr_remarks', { items: clean })).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['settings', 'ivr_remarks'] }); setDirty(false); toast.success('Call remarks saved'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? e?.message ?? 'Save failed'),
  });

  const update = (idx: number, patch: Partial<IvrRemark>) => { setRows((arr) => arr.map((r, i) => (i === idx ? { ...r, ...patch } : r))); setDirty(true); };
  const remove = (idx: number) => { setRows((arr) => arr.filter((_, i) => i !== idx)); setDirty(true); };
  const move = (idx: number, dir: -1 | 1) => {
    const j = idx + dir; if (j < 0 || j >= rows.length) return;
    const next = [...rows]; [next[idx], next[j]] = [next[j], next[idx]]; setRows(next); setDirty(true);
  };
  const add = () => {
    const nextId = rows.length ? Math.max(...rows.map((r) => r.id)) + 1 : 1;
    setRows((arr) => [...arr, { id: nextId, name: '' }]); setDirty(true);
  };

  const doExport = () => {
    if (!rows.length) return;
    const lines = ['id,name', ...rows.map((r) => {
      const s = String(r.name ?? '');
      return `${r.id},${/[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s}`;
    })];
    const url = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `ivr-remarks-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);   // revoking in the same tick cancels the save
  };

  if (isLoading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">IVR Call Remarks</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            The <strong>Customer Remarks</strong> dropdown an agent picks from after a call, on the IVR screen.
            The free-text note on the call is separate and still carries the detail.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {rows.length === 0 && (
            <button onClick={() => { setRows(DEFAULTS); setDirty(true); }} className="px-3 py-2 text-sm text-primary underline">
              Load {DEFAULTS.length} suggested remarks
            </button>
          )}
          <button onClick={doExport} disabled={!rows.length}
            className="px-3 py-2 text-sm border border-emerald-300 text-emerald-700 rounded-lg hover:bg-emerald-50 flex items-center gap-2 disabled:opacity-50">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={add} className="px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center gap-2">
            <Plus size={14} /> Add Remark
          </button>
          <button onClick={() => save.mutate()} disabled={!dirty || save.isPending}
            className="px-4 py-2 bg-primary text-white text-sm rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      </div>

      {rows.length === 0 && (
        <div className="border border-dashed border-slate-300 rounded-lg p-8 text-center text-sm text-slate-500">
          No call remarks defined yet, so the IVR dropdown is empty.
          Click <strong>Load {DEFAULTS.length} suggested remarks</strong> to start, then edit them to suit your desk.
        </div>
      )}

      {rows.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 w-20">Sort</th>
                <th className="text-left px-3 py-2 w-16">ID</th>
                <th className="text-left px-3 py-2">Remark shown in the dropdown</th>
                <th className="px-3 py-2 w-12" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-0.5">
                      <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronUp size={14} /></button>
                      <button onClick={() => move(idx, 1)} disabled={idx === rows.length - 1} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronDown size={14} /></button>
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" value={r.id} onChange={(e) => update(idx, { id: Number(e.target.value) })}
                      className="w-14 px-1.5 py-1 border border-slate-300 rounded text-sm font-mono focus:border-primary outline-none" />
                  </td>
                  <td className="px-2 py-1">
                    <input value={r.name} onChange={(e) => update(idx, { name: e.target.value })} maxLength={200}
                      placeholder="e.g. Customer asked to re-attempt"
                      className="w-full px-2 py-1 border border-slate-300 rounded text-sm focus:border-primary outline-none" />
                  </td>
                  <td className="px-2 py-1">
                    <button onClick={() => remove(idx)} className="p-1.5 text-slate-400 hover:text-danger hover:bg-danger/10 rounded"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-slate-400">
        Renaming a remark does not change the calls already saved under the old wording — each call keeps
        the text that was chosen at the time, so an old report still says what the agent actually picked.
      </p>
    </div>
  );
}
